import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import EpisodeCard from "@/components/feature/EpisodeCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { allEpisodes, pageSize, sortEpisodes, type SortOrder } from "@/lib/catalog";

type FilterKey = "all" | "latest" | "favorites";

const filters: { key: FilterKey; label: string }[] = [
  { key: "all", label: "全部剧集" },
  { key: "latest", label: "最新更新" },
  { key: "favorites", label: "我的收藏" },
];

const sortOptions: { key: SortOrder; label: string }[] = [
  { key: "update", label: "按更新顺序" },
  { key: "number-asc", label: "集数从低到高" },
  { key: "number-desc", label: "集数从高到低" },
];

export default function EpisodesPage() {
  usePageMeta(
    "朋友的妈妈4全集列表 - 剧集信息与观看入口",
    "列出朋友的妈妈4所有剧集，并提供详细信息与简介，方便用户浏览具体章节。",
    "朋友的妈妈4全集,朋友的妈妈4剧集列表,朋友的妈妈4分集剧情,朋友的妈妈4在线观看",
  );

  const { favoriteIds } = useFavorites();
  const [filter, setFilter] = useState<FilterKey>("all");
  const [sortOrder, setSortOrder] = useState<SortOrder>("update");
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [sheetOpen, setSheetOpen] = useState(false);

  const filtered = useMemo(() => {
    if (filter === "latest") {
      return allEpisodes.filter((episode) => episode.status === "最新更新");
    }
    if (filter === "favorites") {
      return allEpisodes.filter((episode) => favoriteIds.includes(episode.id));
    }
    return allEpisodes;
  }, [filter, favoriteIds]);

  const sorted = useMemo(() => sortEpisodes(filtered, sortOrder), [filtered, sortOrder]);
  const visible = sorted.slice(0, visibleCount);
  const hasMore = visibleCount < sorted.length;

  const changeFilter = (key: FilterKey) => {
    setFilter(key);
    setVisibleCount(pageSize);
    setSheetOpen(false);
  };

  const changeSort = (key: SortOrder) => {
    setSortOrder(key);
    setVisibleCount(pageSize);
    setSheetOpen(false);
  };

  const currentSortLabel =
    sortOptions.find((option) => option.key === sortOrder)?.label ?? "按更新顺序";

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="max-w-[1280px] mx-auto px-4 md:px-8 pt-24 md:pt-28 pb-10">
        <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-600">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-600" />
          <span className="text-foreground-900">剧集</span>
        </nav>

        <header className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-wide text-foreground-950">
              全部剧集
            </h1>
            <p className="mt-3 text-sm leading-7 text-foreground-500 max-w-2xl">
              朋友的妈妈4 全 16 集目录，按集数排列，包含每集简介、时长与首播日期。
              点击任意剧集卡片即可进入详情页查看完整剧情与演示播放区。
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-film-line text-accent-500" />
              共 {allEpisodes.length} 集
            </span>
            <span className="w-px h-3 bg-background-300/60" />
            <span className="flex items-center gap-1.5">
              <i className="ri-checkbox-circle-line text-accent-500" />
              已完结
            </span>
          </div>
        </header>

        <div className="mt-8 rounded-lg bg-background-100 border border-background-200/70 p-3 md:p-4">
          <div className="hidden md:flex items-center justify-between gap-4">
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-200/60">
              {filters.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => changeFilter(item.key)}
                  className={`px-4 h-8 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    filter === item.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="sort-select"
                className="text-xs text-foreground-600 whitespace-nowrap"
              >
                排序方式
              </label>
              <select
                id="sort-select"
                value={sortOrder}
                onChange={(event) => changeSort(event.target.value as SortOrder)}
                className="h-9 pl-3 pr-8 rounded-md bg-background-200/70 border border-background-300/60 text-xs text-foreground-900 cursor-pointer focus:outline-none focus:border-primary-500"
              >
                {sortOptions.map((option) => (
                  <option key={option.key} value={option.key} className="bg-background-100">
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-md bg-background-200/70 border border-background-300/60 text-xs text-foreground-800 cursor-pointer"
            >
              <i className="ri-filter-3-line" />
              {filters.find((item) => item.key === filter)?.label}
            </button>
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-md bg-background-200/70 border border-background-300/60 text-xs text-foreground-800 cursor-pointer"
            >
              <i className="ri-sort-desc" />
              {currentSortLabel}
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-foreground-600">
          <p>
            已显示 <span className="text-foreground-900">{visible.length}</span> / {sorted.length} 集
          </p>
          <p className="hidden sm:block">{currentSortLabel}</p>
        </div>

        {sorted.length === 0 ? (
          <div className="mt-8 rounded-lg bg-background-100 border border-background-200/70 py-16 px-6 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-background-200/70 text-foreground-500 flex items-center justify-center">
              <i className="ri-inbox-line text-2xl" />
            </div>
            <h3 className="mt-5 font-heading text-lg text-foreground-950">
              暂无相关剧集，稍后再试
            </h3>
            <p className="mt-2 text-sm text-foreground-500">
              {filter === "favorites"
                ? "你的收藏列表还是空的，可以先去剧集详情页点击收藏按钮。"
                : "当前筛选条件下没有内容，试试切换到全部剧集。"}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => changeFilter("all")}
                className="h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部剧集
              </button>
              <Link
                to="/"
                className="h-10 px-5 rounded-md border border-background-300/60 text-foreground-800 text-sm hover:border-primary-500 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center"
              >
                返回首页
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
              {visible.map((episode, index) => (
                <EpisodeCard key={episode.id} episode={episode} index={index} />
              ))}
            </div>

            <div className="mt-8 flex flex-col items-center gap-3">
              {hasMore ? (
                <button
                  type="button"
                  onClick={() => setVisibleCount((count) => count + pageSize)}
                  className="inline-flex items-center gap-2 h-11 px-7 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-900 hover:border-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line" />
                  加载更多（还有 {sorted.length - visibleCount} 集）
                </button>
              ) : (
                <p className="text-xs text-foreground-600">已经到底啦，全部 {sorted.length} 集都在这里。</p>
              )}
            </div>
          </>
        )}
      </main>

      {sheetOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setSheetOpen(false)}
            className="absolute inset-0 bg-background-950/70 backdrop-blur-sm cursor-pointer"
          />
          <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-background-100 border-t border-background-200 p-5 pb-8 anim-fade-up">
            <div className="w-10 h-1 rounded-full bg-background-400 mx-auto" />
            <div className="mt-4 flex items-center justify-between">
              <h3 className="font-heading text-base text-foreground-950">筛选与排序</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setSheetOpen(false)}
                className="w-8 h-8 rounded-md bg-background-200/70 text-foreground-700 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <p className="mt-5 text-[11px] tracking-[0.2em] text-foreground-600 uppercase">
              剧集分类
            </p>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {filters.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => changeFilter(item.key)}
                  className={`h-10 rounded-md text-xs cursor-pointer whitespace-nowrap transition-colors ${
                    filter === item.key
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-200/70 text-foreground-700"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <p className="mt-5 text-[11px] tracking-[0.2em] text-foreground-600 uppercase">
              排序方式
            </p>
            <div className="mt-2 flex flex-col gap-2">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => changeSort(option.key)}
                  className={`h-11 px-4 rounded-md text-sm flex items-center justify-between cursor-pointer ${
                    sortOrder === option.key
                      ? "bg-accent-100 text-foreground-950 border border-accent-500/50"
                      : "bg-background-200/60 text-foreground-700"
                  }`}
                >
                  {option.label}
                  {sortOrder === option.key && (
                    <i className="ri-check-line text-accent-500" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}